import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { PERMISSIONS, can, type PermissionKey, type UserRole } from "@devengo/shared";

import {
  Badge,
  Button,
  Card,
  ErrorBox,
  Field,
  Input,
  Notice,
  PageTitle,
  Select,
  tableClass,
  tdClass,
  thClass,
} from "../components/ui";
import { api } from "../lib/api";
import { applyBranding, type TenantBranding } from "../lib/branding";
import { ROLE_LABELS, useBranding, useCurrentUser } from "../lib/session";

interface StudioUser {
  id: string;
  email: string;
  name: string;
  role: keyof typeof ROLE_LABELS;
  status: "active" | "inactive";
  /** Invited by mail and the link not used yet. */
  invitationPending: boolean;
}

export function StudioPage() {
  const user = useCurrentUser();
  if (!can(user.role, "manageUsers")) {
    return (
      <>
        <PageTitle>Estudio</PageTitle>
        <Card>
          <p className="text-sm text-brand-muted">
            La configuración del estudio la maneja el Dueño.
          </p>
        </Card>
      </>
    );
  }
  return (
    <>
      <PageTitle subtitle="La marca que ven tus clientes en pantallas y documentos, y quién trabaja en el sistema.">
        Estudio
      </PageTitle>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <BrandingCard />
        <UsersCard />
      </div>
    </>
  );
}

function BrandingCard() {
  const branding = useBranding();
  const [name, setName] = useState(branding.displayName);
  const [color, setColor] = useState(branding.primaryColor);
  const [address, setAddress] = useState(branding.contact.address ?? "");
  const [phone, setPhone] = useState(branding.contact.phone ?? "");
  const [email, setEmail] = useState(branding.contact.email ?? "");
  const save = useMutation({
    mutationFn: () =>
      api.patch<TenantBranding>("/api/tenant/branding", {
        displayName: name,
        primaryColor: color,
        contact: {
          ...(address ? { address } : {}),
          ...(phone ? { phone } : {}),
          ...(email ? { email } : {}),
        },
      }),
    onSuccess: (updated) => applyBranding(updated),
  });
  return (
    <Card title="Marca">
      <div className="flex flex-col gap-3">
        <Field label="Nombre del estudio">
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Color principal" hint="Se aplica a pantallas, libros y PDF">
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="h-9 w-14 rounded border border-brand-line"
            />
            <Input value={color} onChange={(e) => setColor(e.target.value)} className="w-32" />
          </div>
        </Field>
        <Field label="Dirección">
          <Input value={address} onChange={(e) => setAddress(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Teléfono">
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
          </Field>
          <Field label="Correo">
            <Input value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
        </div>
        <ErrorBox error={save.error} />
        {save.isSuccess ? (
          <Notice tone="success">
            Marca actualizada. Los próximos PDF ya salen con estos datos.
          </Notice>
        ) : null}
        <div>
          <Button disabled={save.isPending} onClick={() => save.mutate()}>
            Guardar marca
          </Button>
        </div>
      </div>
    </Card>
  );
}

function UsersCard() {
  const me = useCurrentUser();
  const client = useQueryClient();
  const users = useQuery({
    queryKey: ["users"],
    queryFn: () => api.get<StudioUser[]>("/api/users"),
  });
  const mode = useQuery({
    queryKey: ["users", "invitation-mode"],
    queryFn: () => api.get<{ invitesByMail: boolean }>("/api/users/invitation-mode"),
  });
  const byMail = mode.data?.invitesByMail ?? true;
  const [form, setForm] = useState({ email: "", name: "", role: "operador", password: "" });
  const [invitedTo, setInvitedTo] = useState<string | null>(null);
  const refresh = () => client.invalidateQueries({ queryKey: ["users"] });
  // With mail, people are invited and choose their own password; without it, the Dueño types an
  // initial one (plan de seguridad).
  const create = useMutation({
    mutationFn: () =>
      byMail
        ? api.post<StudioUser>("/api/users/invitations", {
            email: form.email,
            name: form.name,
            role: form.role,
          })
        : api.post<StudioUser>("/api/users", form),
    onSuccess: async (user) => {
      setInvitedTo(byMail ? user.email : null);
      setForm({ email: "", name: "", role: "operador", password: "" });
      await refresh();
    },
  });
  const resend = useMutation({
    mutationFn: (id: string) => api.post<{ email: string }>(`/api/users/${id}/invitation`),
    onSuccess: (result) => setInvitedTo(result.email),
  });
  const update = useMutation({
    mutationFn: (input: { id: string; patch: object }) =>
      api.patch(`/api/users/${input.id}`, input.patch),
    onSuccess: refresh,
  });
  return (
    <Card title="Usuarios">
      <table className={tableClass}>
        <thead>
          <tr>
            <th className={thClass}>Nombre</th>
            <th className={thClass}>Rol</th>
            <th className={thClass}>Estado</th>
          </tr>
        </thead>
        <tbody>
          {users.data?.map((u) => (
            <tr key={u.id}>
              <td className={tdClass}>
                <div>{u.name}</div>
                <div className="text-xs text-brand-muted">{u.email}</div>
                {u.invitationPending ? (
                  <div className="mt-1 flex items-center gap-2">
                    <Badge tone="warning">Invitación pendiente</Badge>
                    <button
                      type="button"
                      className="text-xs text-brand-primary underline"
                      disabled={resend.isPending}
                      onClick={() => resend.mutate(u.id)}
                    >
                      Reenviar invitación
                    </button>
                  </div>
                ) : null}
              </td>
              <td className={tdClass}>
                <Select
                  value={u.role}
                  disabled={u.id === me.userId || update.isPending}
                  onChange={(e) => update.mutate({ id: u.id, patch: { role: e.target.value } })}
                >
                  {Object.entries(ROLE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </td>
              <td className={tdClass}>
                {u.id === me.userId ? (
                  <Badge tone="success">Vos</Badge>
                ) : (
                  <Button
                    variant="secondary"
                    disabled={update.isPending}
                    onClick={() =>
                      update.mutate({
                        id: u.id,
                        patch: { status: u.status === "active" ? "inactive" : "active" },
                      })
                    }
                  >
                    {u.status === "active" ? "Desactivar" : "Activar"}
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <ErrorBox error={update.error ?? resend.error} />
      {invitedTo ? (
        <Notice tone="success">
          Le mandamos la invitación a {invitedTo}. El enlace vence en 48 horas; la persona elige su
          propia clave.
        </Notice>
      ) : null}
      <div className="mt-4 grid grid-cols-2 gap-2 border-t border-brand-line pt-3">
        <Field label="Nombre">
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label="Correo">
          <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </Field>
        <Field label="Rol" hint={ROLE_SUMMARIES[form.role as UserRole]}>
          <Select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
            {Object.entries(ROLE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </Field>
        {byMail ? (
          <p className="self-end text-xs text-brand-muted">
            Le llega un mail para elegir su clave. Nadie más la conoce.
          </p>
        ) : (
          <Field
            label="Clave inicial"
            hint="El estudio todavía no puede mandar mails: pasásela por un canal seguro"
          >
            <Input
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
          </Field>
        )}
        <div className="col-span-2">
          <ErrorBox error={create.error} />
          <Button className="mt-2" disabled={create.isPending} onClick={() => create.mutate()}>
            {byMail ? "Invitar por mail" : "Agregar usuario"}
          </Button>
        </div>
      </div>
      <RolesMatrix highlight={form.role as UserRole} />
    </Card>
  );
}

const ROLE_SUMMARIES: Record<UserRole, string> = {
  dueno:
    "Todo, incluidos los usuarios, la marca del estudio, reabrir períodos cerrados, la auditoría y los asistentes de IA.",
  contador: "El ciclo fiscal completo: revisión, Caja IVA, cierre de períodos y empresas.",
  operador:
    "Carga e importación de comprobantes en períodos abiertos y contrapartes; consulta lo demás.",
};

const ROLES = Object.keys(ROLE_LABELS) as UserRole[];

/** What each role may do, from the same table the API authorises with. */
function RolesMatrix({ highlight }: { highlight: UserRole }) {
  return (
    <div className="mt-4 border-t border-brand-line pt-3">
      <h3 className="text-sm font-semibold">Roles y permisos</h3>
      <table className={`${tableClass} mt-2`}>
        <thead>
          <tr>
            <th className={thClass}>Permiso</th>
            {ROLES.map((role) => (
              <th
                key={role}
                className={`${thClass} text-center ${role === highlight ? "bg-brand-tint" : ""}`}
              >
                {ROLE_LABELS[role]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(Object.keys(PERMISSIONS) as PermissionKey[]).map((key) => (
            <tr key={key}>
              <td className={tdClass}>{PERMISSIONS[key].label}</td>
              {ROLES.map((role) => (
                <td
                  key={role}
                  className={`${tdClass} text-center ${role === highlight ? "bg-brand-tint" : ""}`}
                >
                  {can(role, key) ? (
                    <span className="font-semibold text-emerald-700" aria-label="Sí">
                      ✓
                    </span>
                  ) : (
                    <span className="text-brand-muted" aria-label="No">
                      —
                    </span>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
